// Cart drawer upsell: free-shipping progress bar, the swipe row's arrows, and the Add button.
//
// Must stay an asset loaded unconditionally from cart-drawer.liquid. Inline in
// cart-upsell.liquid it only shipped in the drawer's non-empty branch, so a
// shopper arriving with an empty cart never ran it, and the markup morphed in
// later carries a <script> the browser will not execute, leaving the Add button
// with no listener. Everything here no-ops when the markup is absent.
//
// WHICH CARDS SHOW, AND IN WHAT ORDER, IS DECIDED IN LIQUID, NOT HERE (2026-09-23).
// snippets/cart-upsell.liquid evaluates the cart rules against cart.items and renders the row in
// its final order. This file deliberately does not reorder or hide cards. An earlier version set
// style.order and toggled is-visible from JS, and every cart change undid it, because the drawer
// is morphed from a fresh server render that resets inline styles and attributes. Server side,
// the morph restores the right state instead of erasing it. That also retired the
// MutationObserver that existed only to re-apply the JS choice after each morph.
// See docs/superpowers/notes/cart-drawer-map.md.

// Upgrades <scroll-hint>. snippets/cart-drawer.liquid already wraps the cart items list in one,
// and scrolling.js is in the importmap and modulepreloaded, but a modulepreload fetches without
// executing and only collection-links, slideshow and zoom-dialog import it. So on any cart page
// without a slideshow that element has been inert. This one line fixes it.
import "@theme/scrolling";

let inFlight = false;
let queued = false;

async function run() {
  // KRF 2026-10-13: the shipping message and bar live in #cd-ship-wrapper at the top of the drawer
  // (snippets/cart-free-shipping.liquid); #cd-upsell-wrapper is the fallback for older markup.
  const wrapper = document.querySelector("#cd-ship-wrapper") || document.querySelector("#cd-upsell-wrapper");
  if (!wrapper) return;

  let cart;
  try {
    cart = await fetch("/cart.js").then((r) => r.json());
  } catch {
    return; // keep whatever is currently visible
  }

  const freeAmount = Number(wrapper.dataset.freeAmount);
  const cartTotal = cart.total_price;
  const left = Math.max(freeAmount - cartTotal, 0);

  if (left === 0) {
    wrapper.setAttribute("data-shipping-free", "");
  } else {
    wrapper.removeAttribute("data-shipping-free");
    const leftEl = wrapper.querySelector("#cd-upsell-left");
    if (leftEl) leftEl.textContent = `$${(left / 100).toFixed(2)}`;
  }
  const barFill = wrapper.querySelector(".cd-upsell-bar-fill");
  if (barFill) barFill.style.width = `${Math.min((cartTotal / freeAmount) * 100, 100)}%`;

  syncArrows();
}

/* Arrow disabled state follows the scroll position. The server renders prev disabled and next
   enabled, which is correct at scroll 0, and this keeps them honest after that. Visibility of the
   arrows is decided server side from the card count, so nothing here shows or hides them. */
function syncArrows() {
  const row = document.querySelector("[data-upsell-row]");
  if (!row) return;
  const prev = document.querySelector("[data-upsell-prev]");
  const next = document.querySelector("[data-upsell-next]");
  const max = row.scrollWidth - row.clientWidth;
  if (prev) prev.disabled = row.scrollLeft <= 2;
  if (next) next.disabled = row.scrollLeft >= max - 2;
}

async function updateUpsellSection() {
  if (inFlight) {
    queued = true; // coalesce: one follow-up run, not one per trigger
    return;
  }
  inFlight = true;
  try {
    await run();
  } finally {
    inFlight = false;
    if (queued) {
      queued = false;
      updateUpsellSection();
    }
  }
}

// Module scripts are deferred, so the DOM is ready here.
updateUpsellSection();

document.addEventListener("cart-drawer:open", updateUpsellSection);
document.addEventListener("cart:update", updateUpsellSection);

// Delegated so it survives section morphs. RESTORED 2026-09-23: this handler was dropped
// during the cart-aware rewrite, which left every Add button in the upsell inert. Delegation
// on document is deliberate, because the drawer replaces this markup wholesale on every cart
// change and a handler bound to the button would not survive that.
document.addEventListener("click", async (e) => {
  const btn = e.target.closest(".cd-upsell-btn");
  if (!btn) return;

  btn.disabled = true;
  try {
    await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: btn.dataset.variant, quantity: 1 }),
    });

    const { CartUpdateEvent } = await import("@theme/events").catch(() => ({}));
    const cartRes = await fetch("/cart.js");
    const cart = await cartRes.json();

    if (CartUpdateEvent) {
      document.dispatchEvent(
        new CartUpdateEvent(cart, "", {
          itemCount: cart.item_count,
          source: "cart-items-component",
        })
      );
    }

    updateUpsellSection();
  } finally {
    btn.disabled = false;
  }
});

// Arrow clicks, delegated so they survive the drawer morphing this markup back in. Handled on
// window in the capture phase and stopped there: bar-promo-pricing.js has a document capture
// listener that marks the whole cart busy (dimmed, pointer-events: none) on any button click
// inside cart-items-component and only clears it on the next cart update or its 5s timer. An
// arrow only scrolls the row, so it must not reach that listener.
window.addEventListener("click", (e) => {
  const btn = e.target instanceof Element && e.target.closest("[data-upsell-prev], [data-upsell-next]");
  if (!btn) return;
  e.stopPropagation();
  const row = document.querySelector("[data-upsell-row]");
  if (!row) return;
  const cell = row.querySelector(".cd-upsell-cell");
  const step = cell ? cell.getBoundingClientRect().width + 10 : 210;
  const dir = btn.hasAttribute("data-upsell-next") ? 1 : -1;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  row.scrollBy({ left: step * dir, behavior: reduce ? "auto" : "smooth" });
}, true);

// Keep the arrow disabled states in step with the scroll position. Capture phase, because scroll
// does not bubble.
document.addEventListener(
  "scroll",
  (e) => {
    if (e.target instanceof Element && e.target.matches("[data-upsell-row]")) syncArrows();
  },
  true
);
